import React, { useEffect, useRef } from "react";

// SASS
import "./Part6.scss";

// COMPONENTS
import Step from "../Step/Step";

// HELPERS
import animate from "../../utils/animate";

// LIBRARIES
import scrollama from "scrollama";

// ASSETS
import examRoomSceneOneImg from "../../assets/img/exam-room-scene-1.png";
import examRoomSceneTwoImg from "../../assets/img/exam-room-scene-2.png";
import examRoomSceneThreeImg from "../../assets/img/exam-room-scene-3.png";
import examRoomSceneFourImg from "../../assets/img/exam-room-scene-4.png";
import examRoomSceneFiveImg from "../../assets/img/exam-room-scene-5.png";
import ceilingImg from "../../assets/img/ceiling.svg";
import tableImg from "../../assets/img/tabletop.svg";
import nurseHandsImg from "../../assets/img/nurse-1-glowing-hands.png";
import motherBabyStateOneImg from "../../assets/img/mother-child.png";
import motherBabyStateTwoImg from "../../assets/img/mother-child-highlight-1.png";
import motherBabyStateThreeImg from "../../assets/img/mother-child-highlight-2.png";
import germ1Img from "../../assets/img/germ-1-orange.svg";
import germ2Img from "../../assets/img/germ-6-orange.svg";
import germ3Img from "../../assets/img/germ-3-orange.svg";

export default function Part6({ step }) {
  // SCROLLAMA INSTANCE
  const scrolly = scrollama();

  // ANIMATION STEP DATA
  const animationSequence = [
    { stepNumber: "0", height: null, description: "spacer" },
    {
      stepNumber: "1",
      height: "750px",
      description: "fade in scene 1, fade in top span 1",
    },
    {
      stepNumber: "2",
      height: "750px",
      description: "fade in scene 2, fade in top span 2",
    },
    {
      stepNumber: "3",
      height: "750px",
      description: "fade in scene 3, fade in top span 3",
    },
    {
      stepNumber: "4",
      height: "750px",
      description: "fade in scene 4, fade in top span 4, fade in nurse hands",
    },
    {
      stepNumber: "5",
      height: "750px",
      description:
        "fade in germs container, fade in motherbaby 1, fade in bottom text 1",
    },
    {
      stepNumber: "6",
      height: "750px",
      description: "fade in motherbaby 2, fade in bottom text 2",
    },
    {
      stepNumber: "7",
      height: "750px",
      description: "fade in motherbaby 3, fade in bottom text 3",
    },
    { stepNumber: "8", height: null, description: "spacer" },
  ];

  // REFS
  const examRoomSceneTwoEl = useRef(null);
  const examRoomSceneThreeEl = useRef(null);
  const examRoomSceneFourEl = useRef(null);
  const examRoomSceneFiveEl = useRef(null);
  const germsContainerEl = useRef(null);
  const nurseHandsEl = useRef(null);
  const motherBabyOneEl = useRef(null);
  const motherBabyTwoEl = useRef(null);
  const motherBabyThreeEl = useRef(null);
  const topTextSpanOneEl = useRef(null);
  const topTextSpanTwoEl = useRef(null);
  const topTextSpanThreeEl = useRef(null);
  const topTextSpanFourEl = useRef(null);
  const bottomTextOneEl = useRef(null);
  const bottomTextTwoEl = useRef(null);
  const bottomTextThreeEl = useRef(null);

  // RUN WHEN THE COMPONENT MOUNTS
  useEffect(() => {
    scrolly
      .setup({
        step: `.${step}`,
        debug: false,
        threshold: 1,
        progress: true,
      })
      .onStepEnter(({ element, direction }) => {
        const step = element.dataset.step;
        switch (step) {
          case "1": {
            animate.fadeInOnEnter(topTextSpanOneEl.current, direction);
            animate.fadeInOnEnter(examRoomSceneTwoEl.current, direction);
            break;
          }
          case "2": {
            animate.fadeInOnEnter(topTextSpanTwoEl.current, direction);
            animate.fadeInOnEnter(examRoomSceneThreeEl.current, direction);
            break;
          }
          case "3": {
            animate.fadeInOnEnter(topTextSpanThreeEl.current, direction);
            animate.fadeInOnEnter(examRoomSceneFourEl.current, direction);
            break;
          }
          case "4": {
            animate.fadeInOnEnter(topTextSpanFourEl.current, direction);
            animate.fadeInOnEnter(examRoomSceneFiveEl.current, direction);
            animate.fadeInOnEnter(nurseHandsEl.current, direction);
            break;
          }
          case "5": {
            animate.fadeInOnEnter(germsContainerEl.current, direction);
            animate.fadeInOnEnter(motherBabyOneEl.current, direction);
            animate.fadeInOnEnter(bottomTextOneEl.current, direction);
            break;
          }
          case "6": {
            animate.fadeOutOnEnter(bottomTextOneEl.current, direction);
            animate.fadeInOnEnter(bottomTextTwoEl.current, direction);
            animate.fadeInOnEnter(motherBabyTwoEl.current, direction);
            break;
          }
          case "7": {
            animate.fadeOutOnEnter(bottomTextTwoEl.current, direction);
            animate.fadeInOnEnter(bottomTextThreeEl.current, direction);
            animate.fadeInOnEnter(motherBabyThreeEl.current, direction);
            break;
          }
          default:
            break;
        }
      })
      .onStepProgress(({ element, progress }) => {
        const step = element.dataset.step;
        switch (step) {
          default:
            break;
        }
      })
      .onStepExit(({ element, direction }) => {
        const step = element.dataset.step;
        switch (step) {
          case "1": {
            animate.fadeOutOnExit(topTextSpanOneEl.current, direction);
            animate.fadeOutOnExit(examRoomSceneTwoEl.current, direction);
            break;
          }
          case "2": {
            animate.fadeOutOnExit(topTextSpanTwoEl.current, direction);
            animate.fadeOutOnExit(examRoomSceneThreeEl.current, direction);
            break;
          }
          case "3": {
            animate.fadeOutOnExit(topTextSpanThreeEl.current, direction);
            animate.fadeOutOnExit(examRoomSceneFourEl.current, direction);
            break;
          }
          case "4": {
            animate.fadeOutOnExit(topTextSpanFourEl.current, direction);
            animate.fadeOutOnExit(examRoomSceneFiveEl.current, direction);
            animate.fadeOutOnExit(nurseHandsEl.current, direction);
            break;
          }
          case "5": {
            animate.fadeOutOnExit(germsContainerEl.current, direction);
            animate.fadeOutOnExit(motherBabyOneEl.current, direction);
            animate.fadeOutOnExit(bottomTextOneEl.current, direction);
            break;
          }
          case "6": {
            animate.fadeInOnExit(bottomTextOneEl.current, direction);
            animate.fadeOutOnExit(bottomTextTwoEl.current, direction);
            animate.fadeOutOnExit(motherBabyTwoEl.current, direction);
            break;
          }
          case "7": {
            animate.fadeInOnExit(bottomTextTwoEl.current, direction);
            animate.fadeOutOnExit(bottomTextThreeEl.current, direction);
            animate.fadeOutOnExit(motherBabyThreeEl.current, direction);
            break;
          }
          default:
            break;
        }
      });
  }, [scrolly, step]);

  // CUSTOM FUNCTIONS

  return (
    <section className="wrapper">
      <div className="sticky-container--full">
        {/* STICKY */}
        <div className="part6">
          <div className="scene-background">
            <img src={examRoomSceneOneImg} alt="" className="img-fluid scene" />
            <img
              src={examRoomSceneTwoImg}
              alt=""
              className="img-fluid scene scene-2"
              ref={examRoomSceneTwoEl}
            />
            <img
              src={examRoomSceneThreeImg}
              alt=""
              className="img-fluid scene scene-3"
              ref={examRoomSceneThreeEl}
            />
            <img
              src={examRoomSceneFourImg}
              alt=""
              className="img-fluid scene scene-4"
              ref={examRoomSceneFourEl}
            />
            <img
              src={examRoomSceneFiveImg}
              alt=""
              className="img-fluid scene scene-5"
              ref={examRoomSceneFiveEl}
            />
          </div>
          <div className="ceiling-img">
            <img src={ceilingImg} alt="Ceiling" className="img-fluid" />
          </div>
          <div className="table-img">
            <img src={tableImg} alt="" className="img-fluid" />
          </div>
          <div className="top-text">
            Viruses could be on a{" "}
            <span className="span span-1" ref={topTextSpanOneEl}>
              break room door handle,
            </span>{" "}
            <span className="span span-2" ref={topTextSpanTwoEl}>
              computer keyboard at the nurse’s station,
            </span>{" "}
            <span className="span span-3" ref={topTextSpanThreeEl}>
              chair,
            </span>{" "}
            <span className="span span-4" ref={topTextSpanFourEl}>
              IV pull, or more.
            </span>
          </div>
          <div className="bottom-text">
            <div className="text text-1" ref={bottomTextOneEl}>
              So when you touch these infected surfaces, you can pass the virus
              on to others.
            </div>
            <div className="text text-1" ref={bottomTextTwoEl}>
              Also remember <b>our body fluids, like spit and snot</b>, can get
              into surfaces like linen or other items near a patient.
            </div>
            <div className="text text-1" ref={bottomTextThreeEl}>
              Then, if someone touches that infected surface—{" "}
              <b>without washing their hands afterwards</b> —the virus can
              spread from them to other places and people.
            </div>
          </div>
          <div className="nurse-hands-img" ref={nurseHandsEl}>
            <img
              src={nurseHandsImg}
              alt="Nurse"
              className="img-fluid"
            />
          </div>
          <div className="mother-baby-wrapper">
            <img
              src={motherBabyStateOneImg}
              alt=""
              className="mb mb-1 img-fluid"
              ref={motherBabyOneEl}
            />
            <img
              src={motherBabyStateTwoImg}
              alt=""
              className="mb mb-2 img-fluid"
              ref={motherBabyTwoEl}
            />
            <img
              src={motherBabyStateThreeImg}
              alt=""
              className="mb mb-3 img-fluid"
              ref={motherBabyThreeEl}
            />
          </div>
          <div className="germ-container" ref={germsContainerEl}>
            <div className="germ germ-1 rotate delay-random-1">
              <img src={germ1Img} alt="Germ" className="img-fluid" />
            </div>
            <div className="germ germ-2 rotate delay-random-2">
              <img src={germ2Img} alt="Germ" className="img-fluid" />
            </div>
            <div className="germ germ-3 rotate delay-random-3">
              <img src={germ3Img} alt="Germ" className="img-fluid" />
            </div>
          </div>
        </div>
      </div>

      {/* STEPS */}
      {animationSequence.map(({ stepNumber, height, description }) => {
        return (
          <Step
            stepSection={step}
            stepNumber={stepNumber}
            stepDescription={description}
            height={height}
            key={stepNumber}
          />
        );
      })}
    </section>
  );
}
